import React from "react"
import { withPrefix } from "gatsby"
import Layout from "../../components/layout"
import Hero, { HeroCaption } from "../../components/hero"
import ReadNext from "../../components/read-next"
import { PageColumns, Column } from "../../components/page-column"
import Page from "../../components/page"
import Image from "../../components/image"
import Chart from "../../components/chart"
import { spotlight_figure_1 } from "../../data/charts"
import { StatContainer, Stat } from "../../components/stat"

export default function Covid19() {
  return (
    <Layout>
      <Hero
        title="COVID-19 and the War in Ukraine"
        text="The war in Ukraine has dealt a major blow to the global economy."
        image="in-focus/covid-19/hero.jpg"
        video={withPrefix("/") + "video/covid-19/hero.mp4?v=1"}
      />

      <Page>
        <PageColumns>
          <Column location="left" classes="util__desktop-only">
            <HeroCaption>
              The war in Ukraine has dealt a major blow to the global economy.
            </HeroCaption>
          </Column>

          <Column classes="text-block">
            <p className="dropcap">
              The economic fallout from Russia&rsquo;s invasion of Ukraine is
              another massive setback to the global economy. The toll on Ukraine
              is immense, but the impact stretches far beyond Ukraine&rsquo;s
              borders. The severity of disruptions in commodity markets and to
              supply chains will weigh heavily on macro-financial stability and
              growth, adding to an already-complicated policy environment for
              countries still recovering from the COVID-19 pandemic.
            </p>

            <p>
              Inflation, which had already been rising in many countries as a
              result of supply&ndash;demand imbalances and policy support during
              the pandemic, is likely to remain higher for longer. Financial
              conditions have also tightened significantly, putting pressure on
              a wide range of emerging market and developing
              economies&mdash;through higher borrowing costs and the risk of
              capital outflows.
            </p>

            <p>
              The war in Ukraine may contribute to the dangerous divergence
              between advanced and emerging market and developing economies.
              More broadly, it risks fragmenting the global economy into
              geopolitical blocs with distinct technology standards,
              cross-border payment systems, and reserve currencies. Such a
              tectonic shift represents the most serious challenge to the
              rules-based system that has governed international and economic
              relations for the last 75 years, jeopardizing the gains made over
              the past several decades.
            </p>
          </Column>
        </PageColumns>

        <PageColumns modifiers="page-columns--spacing-medium" media>
          <Column location="right">
            <Image
              path="in-focus/covid-19/covid-19-1.jpg"
              alt="Man walks with a bike in front of a bombed building in Ukraine"
              caption="Ukraine"
            />
          </Column>
        </PageColumns>

        <PageColumns align="center">
          <Column classes="text-block">
            <h2>Stepping up</h2>
            <p>
              In response, the IMF has provided vital financing, real-time
              advice, capacity development, and support to its members.
            </p>
            <p>
              <a href="https://www.imf.org/en/News/Articles/2022/04/08/pr22111-imf-executive-board-approves-establishment-of-a-multi-donor-administered-account-for-ukraine">
                $1.4 billion in emergency financing to Ukraine
              </a>{" "}
              was approved in FY 2022, and&mdash;at the request of several IMF
              member countries&mdash;a special account was established that will
              provide donors with a secure vehicle for directing further
              financial assistance to Ukraine. Support for Ukraine&rsquo;s
              heavily affected neighbors and member countries experiencing
              fragility or conflict is also underway.
            </p>
            <p>
              IMF lending and a{" "}
              <a href="https://www.imf.org/en/News/Articles/2021/07/30/pr21235-imf-governors-approve-a-historic-us-650-billion-sdr-allocation-of-special-drawing-rights">
                historic $650 billion allocation of SDRs
              </a>{" "}
              helped provide much-needed liquidity to countries worldwide, many
              of which have limited fiscal space after the pandemic. More than
              $219 billion in loans to 92 countries has been approved since the
              onset of the pandemic. To facilitate access to emergency
              financing, increases to the cumulative access limits for the
              IMF&rsquo;s emergency financing instruments were{" "}
              <a href="https://www.imf.org/en/News/Articles/2021/12/23/pr21403-imf-executive-board-approves-temporary-extension-of-cumulative-access-limits">
                extended
              </a>{" "}
              through the end of June 2023 (for details see <a href={`${withPrefix("/")}downloads/table_2_2_concessional_lending_facilities.pdf`}>
                  Table 2.2
                </a>).
            </p>
            <p>
              One hundred twenty-six Article IV consultations, six Financial
              System Stability Assessments, and nearly 2,900 virtual technical
              visits were conducted during FY 2022. About 60 percent of the
              IMF&rsquo;s technical assistance during the financial year was
              provided to fragile and conflict-affected states, low-income
              countries, and small states.
            </p>
          </Column>

          <Column location="right">
            <Chart chart={spotlight_figure_1} />
          </Column>
        </PageColumns>

        <PageColumns align="center">
          <Column location="left">
            <StatContainer>
              <p>As of April 2022, only</p>
              <Stat number={7} suffix="%" color="dark-orange" />
              <p>
                of people in low-income developing countries had been fully
                vaccinated, compared with
              </p>
              <Stat
                number={73}
                suffix="%"
                style={{ marginTop: 0 }}
                color="aqua"
              />
              <p>in advanced economies.</p>
            </StatContainer>
          </Column>

          <Column classes="text-block">
            <h2>Global partnerships</h2>
            <p>
              In June 2021, the{" "}
              <a href="https://www.imf.org/en/News/Articles/2021/06/30/pr21201-joint-statement-heads-wb-imf-who-wto-first-meeting-task-force-covid-19-developing-countries">
                IMF joined forces with the World Bank, World Health
                Organization, and World Trade Organization to accelerate access
                to COVID-19 vaccines
              </a>
              , therapeutics, and diagnostics. Led by the heads of the
              institutions, a task force was created to mobilize support and
              funding for a $50 billion proposal by the IMF staff to end the
              COVID pandemic. A global target was set to vaccinate at least 40
              percent of the population in all countries by the end of 2021 and
              70 percent by mid-2022.
            </p>
            <p>
              To meet the target, the task force called on Group of Twenty (G20)
              countries to share more vaccine doses with low- and middle-income
              countries; provide financing, including grants and concessional
              financing; and remove all barriers to exports of inputs for
              finished vaccines, diagnostics, and therapeutics.
            </p>
            <p>
              A{" "}
              <a href="https://www.covid19globaltracker.org/">
                global database
              </a>{" "}
              and country-by-country data
              dashboards were set up to track and monitor progress toward
              targets and improve transparency. Access to essential tools for
              fighting COVID-19, nevertheless, remains very uneven. As of April
              2022, only 7 percent of people in low-income developing countries
              had been fully vaccinated, compared with 73 percent in advanced
              economies. There are similar gaps in access to oxygen, treatments,
              and personal protective equipment.
            </p>
            <p>
              Equipping developing economies to fight the pandemic and prepare
              for future health care needs is in everyone&rsquo;s interest: no
              one is safe until everyone is safe. An{" "}
              <a href="https://www.imf.org/en/Publications/WP/Issues/2022/04/04/A-Global-Strategy-to-Manage-the-Long-Term-Risks-of-COVID-19-516079">
                updated plan
              </a>{" "}
              shows that a modest $15 billion in grants in 2022 and $10 billion
              annually thereafter could greatly strengthen global health
              systems.
            </p>
            <p>
              More recently, the IMF, World Bank, UN World Food Program, and
              World Trade Organization have called for urgent, coordinated
              action on food security and have appealed to countries to avoid
              restricting food or fertilizer exports.
            </p>
            <p>
              In collaboration with partners, the IMF continues to champion
              global cooperation and multilateralism.
            </p>
          </Column>
        </PageColumns>

        <PageColumns>
          <Column classes="text-block">
            <h2>Governance reform</h2>
            <p>
              Progress toward governance reform and a timely and successful
              conclusion of the 16<sup>th</sup> General Review of Quotas are
              crucial for ensuring a strong, quota-based, and adequately
              resourced IMF. The review is expected to build on the{" "}
              <a href="https://www.imf.org/en/News/Articles/2015/09/14/01/49/pr10477">
                {" "}
                2010 agreement
              </a>
              , including efforts to protect quotas and voting shares of the
              IMF&rsquo;s poorest members. The{" "}
              <a href="https://www.imf.org/en/About/Factsheets/Sheets/2016/07/14/12/21/IMF-Quotas">
                current formula
              </a>{" "}
              for determining quotas, which was approved in 2008 and has been
              used as a guide, will also be reviewed.
            </p>
          </Column>
        </PageColumns>
      </Page>

      <ReadNext sectionID="in-focus" pageID="a-more-equitable-recovery" />
    </Layout>
  )
}
